/**
 * Paleta de cores e catálogo de temas ilustrados para os "Campos de Estudo" do Ryan.
 * Cores sólidas e temas são escolhas independentes no formulário (RyanHomePage) e se
 * propagam juntas para o card e para o header da RyanFieldPage (RyanFieldHero).
 */

export interface RyanFieldColor {
  id: string;
  label: string;
  color: string;
}

export interface RyanFieldTheme {
  id: string;
  label: string;
  group: 'sistemas' | 'provas';
}

// 12 tons derivados do hue institucional (~145°, o mesmo de #0D3B0E) + 2 contrapontos
// terrosos (argila/dourado) reservados para temas de prova — nenhum tom "briga" com o
// verde da plataforma, mas há variação suficiente de luminosidade/matiz para distinguir
// muitos campos lado a lado.
export const RYAN_FIELD_COLORS: RyanFieldColor[] = [
  { id: 'pinho', label: 'Pinho', color: 'oklch(28% 0.06 150)' },
  { id: 'floresta', label: 'Floresta', color: 'oklch(34% 0.09 145)' },
  { id: 'musgo', label: 'Musgo', color: 'oklch(42% 0.10 135)' },
  { id: 'cedro', label: 'Cedro', color: 'oklch(46% 0.11 158)' },
  { id: 'petroleo', label: 'Petróleo', color: 'oklch(40% 0.08 202)' },
  { id: 'salvia', label: 'Sálvia', color: 'oklch(56% 0.07 140)' },
  { id: 'oliva', label: 'Oliva', color: 'oklch(48% 0.09 112)' },
  { id: 'jade', label: 'Jade', color: 'oklch(50% 0.11 168)' },
  { id: 'argila', label: 'Argila', color: 'oklch(46% 0.07 55)' },
  { id: 'dourado', label: 'Dourado', color: 'oklch(58% 0.10 85)' },
  { id: 'ardosia', label: 'Ardósia', color: 'oklch(40% 0.03 230)' },
  { id: 'carvao', label: 'Carvão-verde', color: 'oklch(24% 0.015 150)' },
  { id: 'esmeralda', label: 'Esmeralda', color: 'oklch(44% 0.13 155)' },
  { id: 'menta', label: 'Menta', color: 'oklch(64% 0.09 165)' },
  { id: 'limao', label: 'Limão', color: 'oklch(62% 0.14 128)' },
  { id: 'pistache', label: 'Pistache', color: 'oklch(60% 0.08 120)' },
  { id: 'azulejo', label: 'Azulejo', color: 'oklch(46% 0.10 190)' },
  { id: 'oceano', label: 'Oceano', color: 'oklch(34% 0.09 212)' },
  { id: 'ciano', label: 'Ciano-escuro', color: 'oklch(38% 0.08 198)' },
  { id: 'bronze', label: 'Bronze', color: 'oklch(42% 0.08 70)' },
  { id: 'terracota', label: 'Terracota', color: 'oklch(48% 0.09 40)' },
  { id: 'grafite', label: 'Grafite-verde', color: 'oklch(32% 0.02 160)' },
  // Cores mais vivas (chroma bem mais alto que o resto da paleta, mas sem
  // luminosidade extrema) — pedido explícito pra ter opções de roxo/lilás/
  // vermelho/amarelo/ocre/dourado que não pareçam "apagadas" ao lado do
  // verde institucional. Só adiciona no fim: nunca renomear/remover uma cor
  // já existente aqui, campos antigos guardam o `id` direto no banco.
  { id: 'roxo', label: 'Roxo', color: 'oklch(42% 0.17 300)' },
  { id: 'lilas', label: 'Lilás', color: 'oklch(62% 0.11 318)' },
  { id: 'rubi', label: 'Rubi', color: 'oklch(47% 0.19 22)' },
  { id: 'coral', label: 'Coral', color: 'oklch(58% 0.16 32)' },
  { id: 'amarelo', label: 'Amarelo', color: 'oklch(80% 0.15 96)' },
  { id: 'ocre', label: 'Ocre', color: 'oklch(58% 0.13 68)' },
  { id: 'ambar', label: 'Âmbar', color: 'oklch(66% 0.15 64)' },
  // Mais vermelhos distintos (não só um) — cada um num ângulo de matiz e
  // luminosidade diferente pra não parecerem variações do mesmo tom.
  { id: 'carmim', label: 'Carmim', color: 'oklch(45% 0.18 8)' },
  { id: 'cereja', label: 'Cereja', color: 'oklch(50% 0.15 18)' },
  // Vermelhos escuros/discretos — luminosidade baixa (igual às cores mais
  // escuras do resto da paleta, tipo "pinho"/"carvão") em vez de mais chroma:
  // fica com aparência de sangue/vinho, sem nunca parecer um vermelho vivo
  // "gritante" que destoe do resto da identidade visual.
  { id: 'sangue', label: 'Sangue', color: 'oklch(34% 0.14 22)' },
  { id: 'vinho', label: 'Vinho', color: 'oklch(27% 0.10 15)' },
  // Tons pastel — precisam de luminosidade moderada (não muito clara: o
  // texto/ícone branco do card fica ilegível em cima de um fundo quase
  // branco) com chroma um pouco mais alto que o normal, senão o tom some e
  // fica parecendo só "apagado"/cinza em vez de uma cor suave de verdade.
  { id: 'lavanda', label: 'Lavanda', color: 'oklch(68% 0.11 300)' },
  { id: 'rosa-pastel', label: 'Rosa Pastel', color: 'oklch(70% 0.13 15)' },
  { id: 'amarelo-pastel', label: 'Amarelo Pastel', color: 'oklch(76% 0.12 90)' },
  { id: 'pessego', label: 'Pêssego', color: 'oklch(72% 0.12 45)' },
  { id: 'azul-pastel', label: 'Azul Pastel', color: 'oklch(68% 0.09 235)' },
  { id: 'menta-pastel', label: 'Menta Pastel', color: 'oklch(70% 0.10 152)' },
];

const DEFAULT_FIELD_COLOR = RYAN_FIELD_COLORS[1].color; // floresta

export function getRyanFieldColor(colorId?: string): string {
  return RYAN_FIELD_COLORS.find(c => c.id === colorId)?.color || DEFAULT_FIELD_COLOR;
}

export type RyanSubdeckColorTone = 'muito-escuro' | 'escuro' | 'medio' | 'claro' | 'muito-claro';

export interface RyanSubdeckColorOption {
  id: string;
  familyId: string;
  familyLabel: string;
  tone: RyanSubdeckColorTone;
  /** true só pro tom "medio" — é a cor pura do campo (a mesma do header/hero),
      as outras 4 são variações derivadas dela. */
  isParent: boolean;
  color: string;
}

/**
 * Cor do subdeck card organizada por família — pra cada uma das 22 cores de
 * campo, 5 tons (muito-escuro/escuro/médio/claro/muito-claro) derivados via
 * color-mix(). O tom "medio" reaproveita o id original de RYAN_FIELD_COLORS
 * (compatível com subdecks já salvos, que guardam só o id de campo puro) e é
 * a cor "pai" — a cor pura do campo, a mesma usada no header/hero da página
 * — marcada com isParent pro seletor conseguir destacá-la visualmente. Os
 * outros 4 tons são ids novos com sufixo. Existe pra sempre oferecer cores
 * que conversam com a cor do campo, em vez de uma paleta solta e desconectada
 * de 22 cores independentes.
 */
export const RYAN_SUBDECK_COLORS: RyanSubdeckColorOption[] = RYAN_FIELD_COLORS.flatMap(c => [
  { id: `${c.id}-muitoescuro`, familyId: c.id, familyLabel: c.label, tone: 'muito-escuro' as const, isParent: false, color: `color-mix(in oklch, ${c.color} 58%, black)` },
  { id: `${c.id}-escuro`, familyId: c.id, familyLabel: c.label, tone: 'escuro' as const, isParent: false, color: `color-mix(in oklch, ${c.color} 80%, black)` },
  { id: c.id, familyId: c.id, familyLabel: c.label, tone: 'medio' as const, isParent: true, color: c.color },
  { id: `${c.id}-claro`, familyId: c.id, familyLabel: c.label, tone: 'claro' as const, isParent: false, color: `color-mix(in oklch, ${c.color} 80%, white)` },
  { id: `${c.id}-muitoclaro`, familyId: c.id, familyLabel: c.label, tone: 'muito-claro' as const, isParent: false, color: `color-mix(in oklch, ${c.color} 58%, white)` },
]);

export function getRyanSubdeckColor(colorId?: string): string {
  if (!colorId) return getRyanFieldColor();
  const found = RYAN_SUBDECK_COLORS.find(c => c.id === colorId);
  if (found) return found.color;
  return getRyanFieldColor(colorId);
}

/**
 * Mesmas 22 famílias de RYAN_FIELD_COLORS, reordenadas pra colocar a cor
 * deste campo em primeiro lugar — usado nos seletores de cor organizados por
 * família (edição de subdeck, categorias de exibição, cor padrão de
 * subdecks) pra não obrigar o admin a rolar a lista até achar a família que
 * já tem destaque visual (ryan-subdeck-color-group--fieldmatch).
 */
export function getOrderedFieldColorFamilies(fieldColorId?: string): RyanFieldColor[] {
  const id = fieldColorId || 'floresta';
  const idx = RYAN_FIELD_COLORS.findIndex(c => c.id === id);
  if (idx <= 0) return RYAN_FIELD_COLORS;
  return [RYAN_FIELD_COLORS[idx], ...RYAN_FIELD_COLORS.slice(0, idx), ...RYAN_FIELD_COLORS.slice(idx + 1)];
}

export const RYAN_FIELD_THEMES: RyanFieldTheme[] = [
  // Sistemas do corpo
  { id: 'cardiovascular', label: 'Cardiovascular', group: 'sistemas' },
  { id: 'respiratorio', label: 'Respiratório', group: 'sistemas' },
  { id: 'neurologico', label: 'Neurológico', group: 'sistemas' },
  { id: 'digestorio', label: 'Digestório', group: 'sistemas' },
  { id: 'urinario', label: 'Urogenital', group: 'sistemas' },
  { id: 'musculoesqueletico', label: 'Musculoesquelético', group: 'sistemas' },
  { id: 'sentidos', label: 'Sentidos', group: 'sistemas' },
  { id: 'dermatologico', label: 'Dermatológico', group: 'sistemas' },
  { id: 'endocrino', label: 'Endócrino', group: 'sistemas' },
  { id: 'hematologico', label: 'Hematológico', group: 'sistemas' },
  { id: 'reprodutor', label: 'Reprodutor', group: 'sistemas' },
  { id: 'imunologico', label: 'Imunológico', group: 'sistemas' },
  // Provas
  { id: 'default', label: 'Geral', group: 'provas' },
  { id: 'prova', label: 'Prova', group: 'provas' },
  { id: 'integradora', label: 'Integradora', group: 'provas' },
  { id: 'programada', label: 'Programada', group: 'provas' },
  { id: 'osce', label: 'OSCE', group: 'provas' },
  { id: 'metodoscientifico', label: 'Métodos Científico', group: 'provas' },
  { id: 'iesc', label: 'IESC', group: 'provas' },
  { id: 'tutoria', label: 'Tutoria', group: 'provas' },
];

export function getRyanFieldThemeLabel(themeId?: string): string {
  return RYAN_FIELD_THEMES.find(t => t.id === themeId)?.label || 'Geral';
}

export interface RyanFieldTones {
  /** Cor sólida do campo — header e bloco "Continuar de onde parou". */
  base: string;
  /** Tinta clara da mesma matiz — fundo da seção de progresso/abas (nunca branco puro). */
  soft: string;
  /** Variante sólida mais escura da mesma matiz — bloco "Top domínio", distinto do header sem conflitar. */
  secondary: string;
}

/**
 * Deriva tons relacionados à cor do campo via `color-mix()` (nativo do navegador,
 * funciona com qualquer uma das cores oklch() da paleta) — garante que hero, abas
 * e blocos de progresso sempre conversem com o header, nunca uma cor desconexa.
 *
 * `secondary` escurece em direção ao preto (não clareia em direção ao branco):
 * clarear misturava até 80% de branco nas cores já claras da paleta (dourado,
 * sálvia, menta, limão…), resultando num bloco quase pastel com texto branco em
 * cima — ilegível/apagado. Escurecer mantém sempre uma cor sólida e contrastada,
 * não importa quão clara seja a cor de base.
 */
export function getRyanFieldTones(colorId?: string): RyanFieldTones {
  const base = getRyanFieldColor(colorId);
  return {
    base,
    soft: `color-mix(in oklch, ${base} 10%, white)`,
    secondary: `color-mix(in oklch, ${base} 82%, black)`,
  };
}
